import { Combobox } from "@headlessui/react"
import { MapPinIcon } from "@heroicons/react/24/solid"
import capitalize from "lodash/capitalize"
import React, { useEffect, useRef, useState } from "react"
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService"
import { twMerge } from "tailwind-merge"

import { DROPDOWN_CLASSNAMES, DROPDOWN_OPTION_CLASSNAMES } from "../../../components/shared/Inputs"
import LoadingSpinner from "../../../components/shared/LoadingSpinner"
import {
  filterHomepagePlacePredictions,
  getHomepageLocationAutocompleteOptions,
  shouldFetchHomepageLocationPredictions
} from "../../../utils/placePredictionsUtils"
import { stripUSAFromString } from "../../../utils/utils"

const HeroLocationSearchBox = ({ initialLocation = "", isMobile = false }) => {
  const [inputText, setInputText] = useState(initialLocation)
  // Store the typed value before selection to preserve zip codes
  const typedValueRef = useRef(initialLocation)

  const {
    placesService,
    placePredictions: rawPlacePredictions,
    getPlacePredictions,
    isPlacePredictionsLoading
  } = usePlacesService({
    debounce: 300,
    options: { componentRestrictions: { country: ["us", "ca"] } }
  })

  const placePredictions = filterHomepagePlacePredictions(rawPlacePredictions, inputText)

  // Helper function to check if input is a zip code (5 digits)
  const isZipCode = (text) => /^\d{5}$/.test(text?.trim() || "")

  // Helper function to extract zip code from description or place details
  const extractZipCode = (description, place = null) => {
    // Try to get postal code from place address components (most reliable)
    if (place?.address_components) {
      const postalComponent = place.address_components.find((component) => component.types.includes("postal_code"))
      if (postalComponent) {
        return postalComponent.long_name
      }
    }

    // Fallback: Extract zip code from description (5 digits)
    const zipMatch = description?.match(/\b(\d{5})\b/)
    if (zipMatch) {
      return zipMatch[1]
    }

    return null
  }

  // Helper function to reverse geocode coordinates to get zip code
  const reverseGeocodeToZip = async (lat, lng) =>
    new Promise((resolve) => {
      if (typeof window === "undefined" || !window.google?.maps?.Geocoder) {
        resolve(null)
        return
      }

      const geocoder = new window.google.maps.Geocoder()
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === "OK" && results && results.length > 0) {
          const result = results[0]
          const postalComponent = result.address_components.find((component) => component.types.includes("postal_code"))
          if (postalComponent) {
            resolve(postalComponent.long_name)
            return
          }
        }
        resolve(null)
      })
    })

  // Helper function to extract zip code from location string or reverse geocode from coordinates
  const getZipCodeFromLocation = async (locationString, coordinates = null) => {
    // First, check if the location string itself is a zip code
    if (isZipCode(locationString)) {
      return locationString.trim()
    }

    // Second, try to extract zip code from the location string (e.g., "33178, Miami, FL")
    const zipFromString = extractZipCode(locationString || "")
    if (zipFromString) {
      return zipFromString
    }

    // If we have coordinates but no zip code in string, try reverse geocoding
    if (coordinates && Array.isArray(coordinates) && coordinates.length === 2) {
      const zipFromCoords = await reverseGeocodeToZip(coordinates[0], coordinates[1])
      if (zipFromCoords) {
        return zipFromCoords
      }
    }

    // Try to reverse geocode from sessionStorage if available
    if (typeof sessionStorage !== "undefined") {
      const storedLatLng = sessionStorage.getItem("latlng")
      if (storedLatLng && !coordinates) {
        try {
          const parsed = JSON.parse(storedLatLng)
          if (Array.isArray(parsed) && parsed.length === 2) {
            const zipFromStored = await reverseGeocodeToZip(parsed[0], parsed[1])
            if (zipFromStored) {
              return zipFromStored
            }
          }
        } catch (e) {
          // Ignore parsing errors
        }
      }
    }

    return null
  }

  useEffect(() => {
    const updateLocationToZipCode = async () => {
      // Extract zip code from initial location or reverse geocode if coordinates available
      const zipCode = await getZipCodeFromLocation(initialLocation)
      if (zipCode) {
        setInputText(zipCode)
        typedValueRef.current = zipCode
      } else {
        // Fallback: if no zip code found, show the location as-is (but this shouldn't happen often)
        setInputText(initialLocation)
        typedValueRef.current = initialLocation
      }
    }

    updateLocationToZipCode()
  }, [initialLocation])

  useEffect(() => {
    const onNewLocationChosen = async (e) => {
      // Always try to extract zip code from the location and display only that
      const locationString = e.detail
      const zipCode = await getZipCodeFromLocation(locationString)

      // Only update if we found a zip code, or if current input is not a zip code
      if (zipCode) {
        setInputText(zipCode)
        typedValueRef.current = zipCode
      } else if (!isZipCode(typedValueRef.current)) {
        // Fallback: show location if no zip code found and current input isn't already a zip code
        setInputText(locationString)
        typedValueRef.current = locationString
      }
    }
    document.addEventListener("newLocationChosen", onNewLocationChosen)

    return () => {
      document.removeEventListener("newLocationChosen", onNewLocationChosen)
    }
  }, [])

  return (
    <Combobox
      as="div"
      className="relative"
      value={inputText}
      onChange={(item) => {
        if (item) {
          const locationString = stripUSAFromString(item.description)

          // Get place details to extract zip code and coordinates
          placesService.getDetails({ placeId: item.place_id }, async (place, status) => {
            let zipCode = null
            let coordinates = null

            if (status === "OK" && place) {
              // Extract zip code from place details (most reliable)
              zipCode = extractZipCode(item.description, place)

              // Extract coordinates from place geometry
              if (place.geometry?.location) {
                const location = place.geometry.location
                const lat = typeof location.lat === "function" ? location.lat() : location.lat
                const lng = typeof location.lng === "function" ? location.lng() : location.lng
                coordinates = [lat, lng]

                // Store coordinates in sessionStorage
                sessionStorage.setItem("latlng", JSON.stringify(coordinates))

                // Dispatch coordinates event
                const coordinatesEvent = new CustomEvent("newLocationCoordinates", { detail: coordinates })
                document.dispatchEvent(coordinatesEvent)

                // If we didn't get zip code from place details, try reverse geocoding
                if (!zipCode && coordinates) {
                  zipCode = await reverseGeocodeToZip(lat, lng)
                }
              }
            } else {
              // Fallback: try to extract zip code from description
              zipCode = extractZipCode(item.description)
            }

            // Always show zip code in input if available, otherwise keep what user typed if it's a zip code
            const typedValue = typedValueRef.current
            if (zipCode) {
              setInputText(zipCode)
              typedValueRef.current = zipCode
            } else if (isZipCode(typedValue)) {
              // Keep the zip code the user typed if no zip code found from place
              // Input text stays as-is (already set by user typing)
            } else {
              // Fallback: show full location if no zip code found
              setInputText(locationString)
              typedValueRef.current = locationString
            }

            // Dispatch location event for backend (always send full location string)
            const locationEvent = new CustomEvent("newLocationChosen", { detail: locationString })
            document.dispatchEvent(locationEvent)
          })
        }
      }}>
      <div className="relative flex min-w-0 items-center gap-2">
        <MapPinIcon className={`shrink-0 text-gray ${isMobile ? "size-4" : "size-5"}`} />
        <Combobox.Input
          onChange={(evt) => {
            const value = evt.target.value
            setInputText(value)
            typedValueRef.current = value

            if (!shouldFetchHomepageLocationPredictions(value)) {
              getPlacePredictions({ input: "" })
              return
            }

            getPlacePredictions({
              input: value,
              ...getHomepageLocationAutocompleteOptions(value)
            })
          }}
          className={`min-w-0 text-base text-black outline-none placeholder:text-gray ${
            isMobile ? "w-16 max-w-[64px]" : "w-24 max-w-[96px]"
          }`}
          placeholder="Location"
        />
        {isPlacePredictionsLoading && (
          <LoadingSpinner className="absolute right-0 top-0 py-0" spinnerClassName="h-4 w-4" />
        )}
      </div>
      {placePredictions?.length > 0 && !isPlacePredictionsLoading && (
        <Combobox.Options
          className={twMerge(
            DROPDOWN_CLASSNAMES,
            `absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2 ${isMobile ? "w-[280px]" : "w-[320px]"}`
          )}>
          {placePredictions.map((item) => (
            <Combobox.Option key={item.description} className={DROPDOWN_OPTION_CLASSNAMES} value={item}>
              <div
                dangerouslySetInnerHTML={{
                  __html: stripUSAFromString(item.description).replace(
                    new RegExp(inputText, "i"),
                    `<mark style='font-weight: bold'>${capitalize(inputText)}</mark>`
                  )
                }}
              />
            </Combobox.Option>
          ))}
        </Combobox.Options>
      )}
    </Combobox>
  )
}

export default HeroLocationSearchBox
